/**
 * shadcn Card — surface primitive for grouped content.
 *
 * Source: `npx shadcn@latest add card` (base-nova style, Tailwind v4).
 * Adapted to match the Berry Street design system Card spec — popover
 * background, 14px radius, 1px border, no wrapper padding (slots own it).
 *
 * Wrapper-only layout: pass content via `children` and let the consumer
 * choose either raw composition (`<Card className="p-6">…</Card>`) or
 * the slot components below.
 *
 * Variants:
 *   default — active card on `bg-popover`
 *   muted   — de-emphasized card on `bg-muted` (e.g. inactive items)
 *
 * @see docs/styling.md
 */
import { cn } from '@web-app/lib/utils';
import { cva, type VariantProps } from 'class-variance-authority';
import * as React from 'react';

const cardVariants = cva(
  'overflow-hidden rounded-[14px] border border-border',
  {
    variants: {
      variant: {
        default: 'bg-popover text-popover-foreground',
        muted: 'bg-muted text-muted-foreground',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  },
);

function Card({
  className,
  variant = 'default',
  ...props
}: React.ComponentProps<'div'> & VariantProps<typeof cardVariants>) {
  return (
    <div
      data-slot="card"
      className={cn(cardVariants({ variant }), className)}
      {...props}
    />
  );
}

function CardHeader({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="card-header"
      className={cn('flex flex-col gap-2 px-6 pt-4', className)}
      {...props}
    />
  );
}

function CardTitle({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="card-title"
      className={cn(
        'text-base leading-6 font-semibold text-foreground',
        className,
      )}
      {...props}
    />
  );
}

function CardDescription({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="card-description"
      className={cn(
        'text-sm leading-5 font-normal text-muted-foreground',
        className,
      )}
      {...props}
    />
  );
}

function CardContent({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="card-content"
      className={cn('px-6 py-4', className)}
      {...props}
    />
  );
}

function CardFooter({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="card-footer"
      className={cn('flex items-center px-6 pb-5', className)}
      {...props}
    />
  );
}

export {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
  cardVariants,
};
